import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/auth.js';
import { api } from '../lib/api.js';
import type { SearchResults } from '@command-floor/shared-types';
import { SubmitRequestButton } from './SubmitRequestButton.js';

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function SearchBar() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debouncedQ = useDebounce(query, 300);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['search', debouncedQ],
    queryFn: () => api.get<SearchResults>(`/api/search?q=${encodeURIComponent(debouncedQ)}`),
    enabled: debouncedQ.length >= 2,
  });

  const hasResults =
    data &&
    (data.tables.length > 0 ||
      data.activities.length > 0 ||
      data.notes.length > 0 ||
      data.files.length > 0 ||
      data.members.length > 0);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function go(path: string) {
    setOpen(false);
    setQuery('');
    navigate(path);
  }

  return (
    <div ref={ref} className="relative w-72">
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search…"
        className="w-full rounded-xl border border-aurora bg-[rgba(6,9,15,0.55)] px-3 py-1.5 text-sm text-slate-200 placeholder-slate-500"
      />

      {open && debouncedQ.length >= 2 && (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-96 w-80 overflow-y-auto rounded border border-aurora-strong bg-slate-900 shadow-xl">
          {!hasResults ? (
            <p className="px-3 py-2 text-sm text-slate-500">No results for "{debouncedQ}"</p>
          ) : (
            <>
              {data!.tables.length > 0 && (
                <ResultGroup label="Work Efforts">
                  {data!.tables.map((t) => (
                    <ResultRow
                      key={t.id}
                      primary={t.name}
                      secondary={t.breadcrumb}
                      onClick={() => go(`/tables/${t.id}`)}
                    />
                  ))}
                </ResultGroup>
              )}
              {data!.activities.length > 0 && (
                <ResultGroup label="Activities">
                  {data!.activities.map((a) => (
                    <ResultRow
                      key={a.id}
                      primary={a.name}
                      secondary={a.table_name}
                      onClick={() => go(`/tables/${a.table_id}`)}
                    />
                  ))}
                </ResultGroup>
              )}
              {data!.notes.length > 0 && (
                <ResultGroup label="Notes">
                  {data!.notes.map((n) => (
                    <ResultRow
                      key={n.id}
                      primary={n.table_name}
                      secondary={n.snippet}
                      onClick={() => go(`/tables/${n.table_id}`)}
                    />
                  ))}
                </ResultGroup>
              )}
              {data!.files.length > 0 && (
                <ResultGroup label="Files">
                  {data!.files.map((f) => (
                    <ResultRow
                      key={f.id}
                      primary={f.filename}
                      secondary={f.table_name}
                      onClick={() => go(`/tables/${f.table_id}`)}
                    />
                  ))}
                </ResultGroup>
              )}
              {data!.members.length > 0 && (
                <ResultGroup label="People">
                  {data!.members.map((m) => (
                    <ResultRow
                      key={m.id}
                      primary={m.display_name}
                      secondary={m.title ?? undefined}
                    />
                  ))}
                </ResultGroup>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      {children}
    </div>
  );
}

function ResultRow({
  primary,
  secondary,
  onClick,
}: {
  primary: string;
  secondary?: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full px-3 py-1.5 text-left hover:bg-slate-800 disabled:cursor-default"
      disabled={!onClick}
    >
      <p className="truncate text-sm text-slate-200">{primary}</p>
      {secondary && <p className="truncate text-xs text-slate-500">{secondary}</p>}
    </button>
  );
}

function ViewToggle() {
  const location = useLocation();
  const navigate = useNavigate();
  const isFloor = location.pathname === '/';
  const isList = location.pathname === '/tables';
  if (!isFloor && !isList) return null;

  function switchTo(path: '/' | '/tables') {
    localStorage.setItem('cf_preferred_view', path === '/' ? 'floor' : 'list');
    navigate(path);
  }

  return (
    <div className="flex overflow-hidden rounded-lg border border-aurora text-xs">
      <button
        onClick={() => switchTo('/')}
        className={`px-3 py-1.5 transition-colors ${isFloor ? 'bg-slate-700 text-slate-100' : 'text-slate-500 hover:text-slate-300'}`}
      >
        Floor
      </button>
      <button
        onClick={() => switchTo('/tables')}
        className={`border-l border-aurora px-3 py-1.5 transition-colors ${isList ? 'bg-slate-700 text-slate-100' : 'text-slate-500 hover:text-slate-300'}`}
      >
        List
      </button>
    </div>
  );
}

function UserChip({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div
      title={name}
      className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-pyek-navy text-xs font-semibold text-slate-200 ring-1 ring-aurora-strong"
    >
      {initials}
    </div>
  );
}

export function TopBar() {
  const { user } = useAuth();
  return (
    <header
      className="flex h-12 flex-none items-center gap-3 border-b border-aurora bg-surface-panel px-4"
      style={{ backdropFilter: 'blur(12px)' }}
    >
      {user && <ViewToggle />}
      {user && <SearchBar />}
      <div className="flex-1" />
      {user && <SubmitRequestButton />}
      {user && <UserChip name={user.displayName} />}
    </header>
  );
}
